// Smart relative-date phrasing for calendar events.
//
// Single-day rules:
//   today + has time, ≥17:00       → "Tonight at 8pm"
//   today + has time, 12-17        → "This afternoon at 3pm"
//   today + has time, <12          → "This morning at 9am"
//   today + no time                → "Today"
//   tomorrow + has time, ≥17:00    → "Tomorrow night at 8pm"
//   tomorrow + has time, 12-17     → "Tomorrow afternoon at 3pm"
//   tomorrow + has time, <12       → "Tomorrow morning at 7:30am"
//   tomorrow + no time             → "Tomorrow"
//   2-6 days out, has time         → "Saturday at 8pm"
//   2-6 days out, no time          → "Saturday"
//   7-13 days out                  → "Next Saturday[, at 8pm]"
//   beyond                         → "April 30[, at 8pm]"
//
// Multi-day rules: both endpoints get a relative label of their own. The
// start carries the time-of-day word ("Tonight", "Tomorrow morning"); the
// end is just a date marker.
//   start today, with time         → "Tonight – Tuesday"
//   start today, no time           → "Today – Tuesday"
//   start tomorrow, with time      → "Tomorrow night – Saturday"
//   start tomorrow, no time        → "Tomorrow – Saturday"
//   2-13 days, both relative       → "Saturday – Next Tuesday"
//   far future, same month         → "April 26 – 28"
//   far future, cross month        → "April 30 – May 2"
//
// Past events (days < 0) just fall through to the "April 30" form.

export interface EventLike {
  startDate: string;        // YYYY-MM-DD
  startTime?: string;       // HH:MM (24h) or empty
  endDate?: string;         // YYYY-MM-DD or empty for single-day
  endTime?: string;         // HH:MM or empty
}

export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Return YYYY-MM-DD `n` days after `iso`. Negative `n` goes backward.
export function addDays(iso: string, n: number): string {
  const d = parseLocal(iso);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function eventSortKey(ev: EventLike): string {
  return `${ev.startDate || '9999-12-31'}T${ev.startTime || '00:00'}`;
}

export function sortEvents<T extends EventLike>(events: T[]): T[] {
  return [...events].sort((a, b) => eventSortKey(a).localeCompare(eventSortKey(b)));
}

export function formatEventDate(ev: EventLike, today: string): string {
  if (!ev.startDate) return '';
  const isMultiDay = !!ev.endDate && ev.endDate !== ev.startDate;
  if (isMultiDay) return formatRange(ev, today);
  return formatSingle(ev.startDate, ev.startTime, today);
}

function formatSingle(date: string, time: string | undefined, today: string): string {
  const days = diffDays(date, today);
  const hasTime = !!time && /^\d{1,2}:\d{2}$/.test(time);

  let dayLabel: string;
  if (days === 0) {
    dayLabel = sameDayLabel(time, hasTime, /*isToday*/ true);
  } else if (days === 1) {
    dayLabel = sameDayLabel(time, hasTime, /*isToday*/ false);
  } else if (days >= 2 && days <= 6) {
    dayLabel = weekday(date);
  } else if (days >= 7 && days <= 13) {
    dayLabel = 'Next ' + weekday(date);
  } else {
    dayLabel = monthDay(date);
  }

  if (!hasTime) return dayLabel;
  return `${dayLabel} at ${formatTime(time!)}`;
}

function sameDayLabel(time: string | undefined, hasTime: boolean, isToday: boolean): string {
  if (!hasTime) return isToday ? 'Today' : 'Tomorrow';
  const h = parseInt(time!.split(':')[0], 10);
  if (isToday) {
    if (h >= 17) return 'Tonight';
    if (h >= 12) return 'This afternoon';
    return 'This morning';
  }
  if (h >= 17) return 'Tomorrow night';
  if (h >= 12) return 'Tomorrow afternoon';
  return 'Tomorrow morning';
}

function formatRange(ev: EventLike, today: string): string {
  const startDate = ev.startDate;
  const endDate = ev.endDate!;
  const startDays = diffDays(startDate, today);
  const endDays = diffDays(endDate, today);
  const hasTime = !!ev.startTime && /^\d{1,2}:\d{2}$/.test(ev.startTime);

  // Far-future + same month: keep the compact "April 26 – 28" form.
  if (startDays > 13 && endDays > 13) {
    const start = parseLocal(startDate);
    const end = parseLocal(endDate);
    const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
    if (sameMonth) {
      return `${start.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} – ${end.getDate()}`;
    }
    return `${monthDay(startDate)} – ${monthDay(endDate)}`;
  }

  const startLabel = relativeLabel(startDate, startDays, ev.startTime, hasTime);
  // The end side is purely a date marker — no time-of-day word, even if today/tomorrow.
  const endLabel = relativeLabel(endDate, endDays, undefined, false);
  return `${startLabel} – ${endLabel}`;
}

function relativeLabel(
  date: string,
  days: number,
  time: string | undefined,
  hasTime: boolean,
): string {
  if (days === 0) return sameDayLabel(time, hasTime, /*isToday*/ true);
  if (days === 1) return sameDayLabel(time, hasTime, /*isToday*/ false);
  if (days >= 2 && days <= 6) return weekday(date);
  if (days >= 7 && days <= 13) return 'Next ' + weekday(date);
  return monthDay(date);
}

function formatTime(time: string): string {
  const [hStr, mStr] = time.split(':');
  let h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  const ampm = h >= 12 ? 'pm' : 'am';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  if (!Number.isFinite(m) || m === 0) return `${h}${ampm}`;
  return `${h}:${pad(m)}${ampm}`;
}

function diffDays(date: string, today: string): number {
  const a = parseLocal(date);
  const b = parseLocal(today);
  return Math.round((a.getTime() - b.getTime()) / 86_400_000);
}

function parseLocal(date: string): Date {
  return new Date(date + 'T00:00:00');
}

function weekday(date: string): string {
  return parseLocal(date).toLocaleDateString('en-US', { weekday: 'long' });
}

function monthDay(date: string): string {
  return parseLocal(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}
